interface LoggerConfig {
  namespaces: Record<string, boolean>
  enabled: boolean
}

const CONFIG_KEY = 'logger_config'
const DEFAULT_CONFIG: LoggerConfig = {
  namespaces: {},
  enabled: false,
}

interface LoggerInstance {
  log: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  debug: (...args: unknown[]) => void
  trace: (...args: unknown[]) => void
}

const styles = {
  log: 'color: lime;',
  info: 'color: aqua;',
  warn: 'color: yellow;',
  error: 'color: red;',
  debug: 'color: fuchsia;',
  trace: 'color: darkmagenta;',
}

class LoggerManager {
  private static config: LoggerConfig = LoggerManager.loadConfig()

  private static loadConfig(): LoggerConfig {
    try {
      if (typeof localStorage === 'undefined') {
        return { ...DEFAULT_CONFIG }
      }
      const stored = localStorage.getItem(CONFIG_KEY)
      if (stored) {
        const config = JSON.parse(stored)
        return { ...DEFAULT_CONFIG, ...config }
      }
    } catch (error) {
      console.error('Failed to load logger config:', error)
    }
    return { ...DEFAULT_CONFIG }
  }

  static reloadConfig(): void {
    LoggerManager.config = LoggerManager.loadConfig()
  }

  private static saveConfig(): void {
    try {
      if (typeof localStorage === 'undefined') {
        return
      }
      localStorage.setItem(CONFIG_KEY, JSON.stringify(LoggerManager.config))
    } catch (error) {
      console.error('Failed to save logger config:', error)
    }
  }

  private static registerNamespace(namespace: string): void {
    if (!(namespace in LoggerManager.config.namespaces)) {
      LoggerManager.config.namespaces[namespace] = false
      LoggerManager.saveConfig()
    }
  }

  private static isEnabled(namespace: string): boolean {
    return LoggerManager.config.enabled && LoggerManager.config.namespaces[namespace] === true
  }

  static createLogger(namespace: string): LoggerInstance {
    LoggerManager.registerNamespace(namespace)

    const createLogMethod = (
      method: keyof Console & keyof typeof styles,
    ): ((...args: unknown[]) => void) => {
      return (...args: unknown[]) => {
        if (method === 'error' || LoggerManager.isEnabled(namespace)) {
          const timestamp = new Date().toLocaleTimeString()
          const prefix = `[${timestamp}] [${namespace}]`
          // eslint-disable-next-line no-console
          const consoleMethod = console[method] as (...args: unknown[]) => void
          consoleMethod(`%c${prefix}\n`, styles[method], ...args)
        }
      }
    }

    return {
      log: createLogMethod('log'),
      info: createLogMethod('info'),
      warn: createLogMethod('warn'),
      error: createLogMethod('error'),
      debug: createLogMethod('debug'),
      trace: createLogMethod('trace'),
    }
  }

  // Static methods for managing configuration
  static getConfig(): LoggerConfig {
    return { ...LoggerManager.config }
  }

  static updateConfig(updates: Partial<LoggerConfig>): void {
    LoggerManager.config = { ...LoggerManager.config, ...updates }
    LoggerManager.saveConfig()
  }

  static enableNamespace(namespace: string, enabled: boolean): void {
    LoggerManager.config.namespaces[namespace] = enabled
    LoggerManager.saveConfig()
  }

  static getAllNamespaces(): string[] {
    return Object.keys(LoggerManager.config.namespaces)
  }

  static setGlobalEnabled(enabled: boolean): void {
    LoggerManager.updateConfig({ enabled })
  }

  static isGlobalEnabled(): boolean {
    return LoggerManager.config.enabled
  }
}

// Create a factory function for backward compatibility
function createLogger(filePath: string): LoggerInstance {
  return LoggerManager.createLogger(filePath)
}

export default createLogger
export { LoggerManager }
